// client/src/components/crowdfunding/CriteriaReview.tsx

import React, { useState, useEffect } from "react";
import { getCriteria, submitCriteriaScores } from "../../services/crowdfundingService";
import "../../assets/styles/crowdfunding.css";

// Define a TypeScript interface for a criterion.
interface Criterion {
  id: string;
  label: string;
  weight: number;
}

const CriteriaReview: React.FC = () => {
  // State to store the dynamic list of criteria fetched from the backend.
  const [criteriaList, setCriteriaList] = useState<Criterion[]>([]);
  // State to hold the scores entered by the user.
  const [criteriaScores, setCriteriaScores] = useState<{ [key: string]: number }>({});
  // States for error, submission status, and loading.
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  // Fetch criteria from backend when the component mounts.
  useEffect(() => {
    const fetchCriteria = async () => {
      try {
        setLoading(true);
        const fetchedCriteria = await getCriteria();
        setCriteriaList(fetchedCriteria);
      } catch (err: any) {
        setError(err.message || "Failed to fetch criteria");
      } finally {
        setLoading(false);
      }
    };

    fetchCriteria();
  }, []);

  // Handler for changes in an individual criterion score.
  const handleScoreChange = (id: string, score: number) => {
    setCriteriaScores((prev) => ({ ...prev, [id]: score }));
  };

  // Handle form submission by validating and calling the service function.
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Validate that every criterion has a score.
    for (const crit of criteriaList) {
      if (criteriaScores[crit.id] === undefined) {
        setError(`Please provide a score for ${crit.label}`);
        return;
      }
    }
    setError(null);

    try {
      // Prepare the payload. Replace "dummy-application-id" with dynamic data.
      const payload = {
        applicationId: "dummy-application-id",
        scores: criteriaScores,
      };
      const result = await submitCriteriaScores(payload);
      console.log("Criteria scores submitted:", result);
      setSubmitted(true);
    } catch (err: any) {
      setError(err.message || "Failed to submit criteria scores");
    }
  };

  if (loading) {
    return <p>Loading criteria...</p>;
  }

  // If no criteria are returned.
  if (criteriaList.length === 0) {
    return <p>No criteria available at the moment.</p>;
  }

  return (
    <div className="criteria-review">
      <h2>Review Your Application Criteria</h2>
      {error && <p style={{ color: "red" }}>{error}</p>}
      {submitted ? (
        <p style={{ color: "green" }}>Criteria reviews submitted successfully!</p>
      ) : (
        <form onSubmit={handleSubmit}>
          {criteriaList.map((criterion) => (
            <div key={criterion.id}>
              <label>
                {criterion.label} (Weight: {criterion.weight}%):
              </label>
              <input
                type="number"
                min="0"
                max="100"
                placeholder="Enter score out of 100"
                value={criteriaScores[criterion.id] ?? ""}
                onChange={(e) =>
                  handleScoreChange(criterion.id, parseInt(e.target.value))
                }
                required
              />
            </div>
          ))}
          <button type="submit">Submit Criteria</button>
        </form>
      )}
    </div>
  );
};

export default CriteriaReview;
